import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { UserLoginDto } from 'src/api/FiretrolApiClient';
import { useUserLogins } from 'src/api/queries/organization/useUserLogins';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { formatDateTime } from 'src/utils';

const getColumns = (): MRT_ColumnDef<UserLoginDto>[] => {
  return [
    {
      accessorKey: 'createdAt',
      header: 'Login Date',
      Cell: ({ cell }) => formatDateTime(cell.getValue<string>()),
    },
    {
      accessorKey: 'ip',
      header: 'IP Address',
    },
  ];
};

interface UserLoginsProps {
  userId?: number;
  userName?: string;
}

export const UserLogins: React.FC<UserLoginsProps> = ({ userId, userName }) => {
  const { data, isLoading, isFetching } = useUserLogins(userId);
  const logins = data ?? [];

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'user-logins',
    columns: columns,
    defaultSorting: [{ id: 'createdAt', desc: true }],
    defaultPagination: {
      pageIndex: 0,
      pageSize: 5,
    },
  });

  const tableExports = useTableExports<UserLoginDto>(
    `${userName}-login-history`,
    (data) => {
      return data.map((item) => ({
        ['Login Date']: formatDateTime(item.createdAt),
        ['IP Address']: item.ip,
      }));
    },
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: false,
      enableClickToCopy: false,
      enableColumnResizing: false,
      enableHiding: false,
      enableDensityToggle: false,
      enableColumnOrdering: false,
      enableFullScreenToggle: false,
      columns: columns,
      data: logins,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    totalCount: logins.length,
    isLoading,
    isFetching,
    isError: false,
    ...tableExports,
  });

  if (logins.length === 0 && !isLoading) {
    return <p>No logins for this user</p>;
  }

  return table;
};
